import React, { FC, useEffect, useState, useRef } from 'react';
import { graphql } from 'gatsby';
import axios from 'axios';
import { GoArrowRight, GoTriangleUp, GoTriangleDown } from 'react-icons/go';
import Skeleton from '@mui/material/Skeleton';
import { handleRipple } from '../../helpers/ripple';
import {
  StyledSection,
  StyledContentBlock,
  MainContainer,
  MainTitle,
  MainSection,
  MainWrapper,
  FirstColumn,
  BitcoinTitle,
  BitcoinText,
  SecondColumn,
  BitcoinPriceContainer,
  PriceTitle,
  PriceContentContainer,
  PriceText,
  GainOrLossTextComparison,
  GraphContainer,
  GraphTitle,
  GraphImage,
  ThirdColumn,
  PriceComparisonContaniner,
  PriceComparisonContentContaniner,
  PriceComparisonContentWrapper,
  BitcoinImage,
  PriceComparisonTitle,
  PriceComparisonText,
  Text,
  PriceContentWrapperComparison,
  PriceTextComparison,
  SliderContainer,
  Slider,
  SliderValue,
  ButtonWrapper,
  ButtonGroup,
  BitcoinButtonContainer,
  BitcoinButton1,
  BitcoinButton2,
  MainWrapperTablet,
  ColumnWrapper,
  CardSection,
  Card,
  CardIcon,
  CardTitle,
} from './styled';

import { remoteLog } from '../../helpers/remoteLog';

interface MarketItem {
  ticker: string;
  monthly_change: number;
  yearly_change: number;
  threeyears_change: number;
  usd_price: number;
  daily_change: number;
}

interface Props {
  // hasScrolled: boolean;
}

type TimeRange = '1M' | '1Y' | '3Y' | 'All time';

const BitcoinPriceSection: FC<Props & Queries.SeoBitcoinPriceSectionFragment> = ({ bitcoinpricesection }) => {
  const [marketItems, setMarketItems] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeButton, setActiveButton] = useState<TimeRange>('1Y');
  const [value, setValue] = useState(500);
  const [ticker, setTicker] = useState('btc');

  const normalizeTicker = (t: string) => {
    if (t.toLowerCase() === 'matic') return 'pol'; // special case mapping
    return t.toLowerCase();
  };

  const bitcoinPages = [
    'alchemy',
    'banxa',
    'mercuryo',
    'moonpay',
    'onramper',
    'ramp',
    'btc',
    'simplex',
    'transak',
    'usdt',
    'usdc',
  ];

  const apiTicker = normalizeTicker(ticker);

  const selectedAsset =
    marketItems.find(asset => asset.ticker.toLowerCase() === apiTicker) ||
    marketItems.find(asset => asset.ticker === 'btc');

  const staticData = {
    Bitcoin: {
      '1M': { gainPercentage: selectedAsset?.monthly_change || 0 },
      '1Y': { gainPercentage: selectedAsset?.yearly_change || 0 },
      '3Y': { gainPercentage: selectedAsset?.threeyears_change || 0 },
      'All time': {
        // eslint-disable-next-line no-unsafe-optional-chaining
        gainPercentage: (selectedAsset?.usd_price ?? 0) * 100 || 0 * 100,
      },
    },
  };

  const sentSliderLogRef = useRef(false);

  const sentinelRef = useRef(null);
  const ProfitScrollSlider = '[Home Page - Profit Calculator] Scroll Slider';
  const ProfitButtonClick = '[Home Page - Profit Calculator] Click Button';

  const fetchMarket = async () => {
    try {
      const urlMarket =
        'https://neo.api.news.bitcoin.com/wp-json/markets/v1/neo_trending_assets?s=bysel';
      const responseMarket = await axios.get(urlMarket, {
        headers: { Accept: 'application/xml' },
      });
      setMarketItems(responseMarket.data.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('There was an error fetching the XML data: ', error);
    }
  };

  const CurrencyFormatter = (amount = 0) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const NumberFormatter = (amount = 0) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    let newValue = 0;

    if (rawValue == null) {
      newValue = 0;
    } else {
      newValue = Math.round(Number(rawValue) / 50) * 50;
    }

    newValue = Math.min(1000, Math.max(1, newValue));
    setValue(newValue);

    if (!sentSliderLogRef.current) {
      sentSliderLogRef.current = true;
      await remoteLog(ProfitScrollSlider);
    }
  };

  const handleButtonClick = async (button: TimeRange) => {
    setActiveButton(button);

    await remoteLog(ProfitButtonClick);
  };

  const calculatePercentage = (price: number) => {
    const min = 1;
    const max = 1000;
    return ((price - min) / (max - min)) * 100;
  };

  const calculateCurrentValue = (gain = 0) => {
    return Number((value * (1 + gain / 100)).toFixed(2));
  };

  const activeData = staticData.Bitcoin[activeButton];
  const bitcoinCurrentValue = calculateCurrentValue(activeData.gainPercentage);

  const isBitcoinGain = (selectedAsset?.daily_change || 0) >= 0;
  const isGain = activeData.gainPercentage >= 0;

  const bitcoinValueText = (timeRange: TimeRange) => {
    switch (timeRange) {
      case 'All time':
        return ` at the beginning `;
      case '3Y':
        return ' three years ago ';
      case '1Y':
        return ' a year ago ';
      case '1M':
        return ' a month ago ';
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchMarket();

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      setTicker(pathParts[0].toLowerCase());
    }
  }, []);

  return (
    <StyledSection>
      <StyledContentBlock>
        {bitcoinpricesection && (
          <MainContainer>
            <MainTitle>{bitcoinpricesection.title}</MainTitle>
            <MainSection>
              <MainWrapper>
                <FirstColumn hasFUSDPage={apiTicker === 'fusd'}>
                  <BitcoinTitle hasFUSDPage={apiTicker === 'fusd'}>
                    {bitcoinpricesection?.bitcoinSection?.title}
                  </BitcoinTitle>
                  <BitcoinText hasFUSDPage={apiTicker === 'fusd'}>
                    {bitcoinpricesection?.bitcoinSection?.text}
                  </BitcoinText>
                </FirstColumn>
                <SecondColumn hasFUSDPage={apiTicker === 'fusd'}>
                  <BitcoinPriceContainer ref={sentinelRef}>
                    <PriceTitle>{bitcoinpricesection.currentPrice}</PriceTitle>
                    {isLoading ? (
                      <PriceContentContainer>
                        <Skeleton variant="rounded" width={200} height={30} />
                        <Skeleton variant="rounded" width={120} height={15} />
                      </PriceContentContainer>
                    ) : (
                      <PriceContentContainer>
                        <PriceText>
                          $
                          {NumberFormatter(
                            Number(selectedAsset?.usd_price ?? 97888),
                          )}
                        </PriceText>
                        <GainOrLossTextComparison>
                          <span
                            className={isBitcoinGain ? 'positive' : 'negative'}
                          >
                            {isBitcoinGain ? (
                              <>
                                <GoTriangleUp size={20} />
                                {NumberFormatter(selectedAsset?.daily_change)}%
                                (1d)
                              </>
                            ) : (
                              <>
                                <GoTriangleDown size={20} />
                                {NumberFormatter(selectedAsset?.daily_change)}%
                                (1d)
                              </>
                            )}
                          </span>
                        </GainOrLossTextComparison>
                      </PriceContentContainer>
                    )}
                  </BitcoinPriceContainer>
                  {bitcoinPages.includes(apiTicker) ? (
                    <GraphContainer>
                      <GraphTitle>{bitcoinpricesection.graphText}</GraphTitle>
                      <GraphImage
                        src={bitcoinpricesection?.graphImage?.src || undefined}
                        alt={bitcoinpricesection?.graphImage?.alt || undefined}
                      />
                    </GraphContainer>
                  ) : (
                    <CardSection hasBitcoinPages={bitcoinPages.includes(apiTicker)}>
                      {bitcoinpricesection?.cards?.map((card, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Card hasBitcoinPages={bitcoinPages.includes(apiTicker)} key={index}>
                          <CardIcon
                            src={card?.image?.src || undefined}
                            alt={card?.image?.alt || undefined}
                          />
                          <CardTitle>{card?.title}</CardTitle>
                        </Card>
                      ))}
                    </CardSection>
                  )}
                </SecondColumn>
                {apiTicker !== 'fusd' && (
                  <ThirdColumn>
                    {isLoading ? (
                      <PriceComparisonContaniner>
                        <PriceComparisonContentContaniner>
                          <PriceComparisonContentWrapper>
                            <Skeleton variant="circular" width={24} height={24} />
                            <Skeleton variant="rounded" width={50} height={15} />
                          </PriceComparisonContentWrapper>
                          <Skeleton variant="rounded" width={150} height={15} />
                          <Skeleton variant="rounded" width={120} height={15} />
                        </PriceComparisonContentContaniner>
                        <PriceContentWrapperComparison>
                          <Skeleton variant="rounded" width={120} height={15} />
                          <Skeleton variant="rounded" width={120} height={15} />
                        </PriceContentWrapperComparison>
                        <SliderContainer>
                          <Skeleton variant="rounded" width={325} height={15} />
                        </SliderContainer>
                        <ButtonWrapper>
                          {['All time', '3Y', '1Y', '1M'].map(label => (
                            <Skeleton
                              key={label}
                              variant="rounded"
                              width={60}
                              height={15}
                            />
                          ))}
                        </ButtonWrapper>
                        <BitcoinButtonContainer>
                          <BitcoinButton1
                            href={
                              bitcoinpricesection?.bitcoinSection?.buttonLink1 ||
                              undefined
                            }
                            target="_blank"
                            onMouseEnter={handleRipple}
                            aria-label="Get Bitcoin"
                          >
                            {bitcoinpricesection?.bitcoinSection?.buttonText1}
                          </BitcoinButton1>
                          <BitcoinButton2
                            href={
                              bitcoinpricesection?.bitcoinSection?.buttonLink2 ||
                              undefined
                            }
                            onMouseEnter={handleRipple}
                            aria-label="Learn more about Bitcoin"
                          >
                            {bitcoinpricesection?.bitcoinSection?.buttonText2}
                            <GoArrowRight size={20} color="#1A1A1A" />
                          </BitcoinButton2>
                        </BitcoinButtonContainer>
                      </PriceComparisonContaniner>
                    ) : (
                      <PriceComparisonContaniner ref={sentinelRef}>
                        <PriceComparisonContentContaniner>
                          <PriceComparisonContentWrapper>
                            <BitcoinImage
                              src={
                                bitcoinpricesection?.priceImage?.src || undefined
                              }
                              alt={
                                bitcoinpricesection?.priceImage?.alt || undefined
                              }
                            />
                            <PriceComparisonTitle>
                              {bitcoinpricesection.cryptoTitle}
                            </PriceComparisonTitle>
                          </PriceComparisonContentWrapper>
                          <PriceComparisonText>
                            bought
                            {bitcoinValueText(activeButton)}
                            with ${value},
                          </PriceComparisonText>
                          <Text>today, would be worth</Text>
                        </PriceComparisonContentContaniner>
                        <PriceContentWrapperComparison>
                          <PriceTextComparison>
                            {CurrencyFormatter(bitcoinCurrentValue)}
                          </PriceTextComparison>
                          <GainOrLossTextComparison>
                            <span className={isGain ? 'positive' : 'negative'}>
                              {isGain ? (
                                <>
                                  <GoTriangleUp size={20} />
                                  {NumberFormatter(activeData.gainPercentage)}%
                                </>
                              ) : (
                                <>
                                  <GoTriangleDown size={20} />
                                  {NumberFormatter(activeData.gainPercentage)}%
                                </>
                              )}
                            </span>
                          </GainOrLossTextComparison>
                        </PriceContentWrapperComparison>
                        <SliderContainer>
                          <Slider
                            type="range"
                            min="1"
                            max="1001"
                            step="50"
                            value={value}
                            onChange={event => {
                              handleChange(event);
                            }}
                            percentageValue={calculatePercentage(value)}
                            aria-label="Profit Calculator Slider"
                          />
                          <SliderValue>${value}</SliderValue>
                        </SliderContainer>
                        <ButtonWrapper>
                          {(selectedAsset?.monthly_change || 0) < 0
                            ? (['All time', '3Y', '1Y'] as TimeRange[]).map(
                                label => (
                                  <ButtonGroup
                                    key={label}
                                    active={activeButton === label}
                                    onClick={() => handleButtonClick(label)}
                                  >
                                    {label}
                                  </ButtonGroup>
                                ),
                              )
                            : (['All time', '3Y', '1Y', '1M'] as TimeRange[]).map(
                                label => (
                                  <ButtonGroup
                                    key={label}
                                    active={activeButton === label}
                                    onClick={() => handleButtonClick(label)}
                                  >
                                    {label}
                                  </ButtonGroup>
                                ),
                              )}
                        </ButtonWrapper>
                        <BitcoinButtonContainer>
                          <BitcoinButton1
                            href={
                              bitcoinpricesection?.bitcoinSection?.buttonLink1 ||
                              undefined
                            }
                            target="_blank"
                            onMouseEnter={handleRipple}
                            aria-label="Get Bitcoin"
                          >
                            {bitcoinpricesection?.bitcoinSection?.buttonText1}
                          </BitcoinButton1>
                          <BitcoinButton2
                            href={
                              bitcoinpricesection?.bitcoinSection?.buttonLink2 ||
                              undefined
                            }
                            onMouseEnter={handleRipple}
                            aria-label="Learn more about Bitcoin"
                          >
                            {bitcoinpricesection?.bitcoinSection?.buttonText2}
                          </BitcoinButton2>
                        </BitcoinButtonContainer>
                      </PriceComparisonContaniner>
                    )}
                  </ThirdColumn>
                )}
              </MainWrapper>
              <MainWrapperTablet hasFUSDPage={apiTicker === 'fusd'}>
                <FirstColumn hasFUSDPage={apiTicker === 'fusd'}>
                  <BitcoinTitle hasFUSDPage={apiTicker === 'fusd'}>
                    {bitcoinpricesection?.bitcoinSection?.title}
                  </BitcoinTitle>
                  <BitcoinText hasFUSDPage={apiTicker === 'fusd'}>
                    {bitcoinpricesection?.bitcoinSection?.text}
                  </BitcoinText>
                </FirstColumn>
                <ColumnWrapper hasFUSDPage={apiTicker === 'fusd'}>
                  <SecondColumn hasFUSDPage={apiTicker === 'fusd'}>
                    <BitcoinPriceContainer ref={sentinelRef}>
                      <PriceTitle>
                        {bitcoinpricesection.currentPrice}
                      </PriceTitle>
                      {isLoading ? (
                        <PriceContentContainer>
                          <Skeleton variant="rounded" width={200} height={30} />
                          <Skeleton variant="rounded" width={120} height={15} />
                        </PriceContentContainer>
                      ) : (
                        <PriceContentContainer>
                          <PriceText>
                            $
                            {NumberFormatter(
                              Number(selectedAsset?.usd_price ?? 97888),
                            )}
                          </PriceText>
                          <GainOrLossTextComparison>
                            <span
                              className={
                                isBitcoinGain ? 'positive' : 'negative'
                              }
                            >
                              {isBitcoinGain ? (
                                <>
                                  <GoTriangleUp size={20} />
                                  {NumberFormatter(selectedAsset?.daily_change)}
                                  %(1d)
                                </>
                              ) : (
                                <>
                                  <GoTriangleDown size={20} />
                                  {NumberFormatter(selectedAsset?.daily_change)}
                                  %(1d)
                                </>
                              )}
                            </span>
                          </GainOrLossTextComparison>
                        </PriceContentContainer>
                      )}
                    </BitcoinPriceContainer>
                    {bitcoinPages.includes(apiTicker) ? (
                      <GraphContainer>
                        <GraphTitle>{bitcoinpricesection.graphText}</GraphTitle>
                        <GraphImage
                          src={bitcoinpricesection?.graphImage?.src || undefined}
                          alt={bitcoinpricesection?.graphImage?.alt || undefined}
                        />
                      </GraphContainer>
                    ) : (
                      <CardSection hasBitcoinPages={bitcoinPages.includes(apiTicker)}>
                        {bitcoinpricesection?.cards?.map((card, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Card hasBitcoinPages={bitcoinPages.includes(apiTicker)} key={index}>
                            <CardIcon
                              src={card?.image?.src || undefined}
                              alt={card?.image?.alt || undefined}
                            />
                            <CardTitle>{card?.title}</CardTitle>
                          </Card>
                        ))}
                      </CardSection>
                    )}
                  </SecondColumn>
                  {apiTicker !== 'fusd' && (
                    <ThirdColumn>
                      {isLoading ? (
                        <PriceComparisonContaniner>
                          <PriceComparisonContentContaniner>
                            <PriceComparisonContentWrapper>
                              <Skeleton
                                variant="circular"
                                width={24}
                                height={24}
                              />
                              <Skeleton
                                variant="rounded"
                                width={50}
                                height={15}
                              />
                            </PriceComparisonContentWrapper>
                            <Skeleton variant="rounded" width={150} height={15} />
                            <Skeleton variant="rounded" width={120} height={15} />
                          </PriceComparisonContentContaniner>
                          <PriceContentWrapperComparison>
                            <Skeleton variant="rounded" width={120} height={15} />
                            <Skeleton variant="rounded" width={120} height={15} />
                          </PriceContentWrapperComparison>
                          <SliderContainer>
                            <Skeleton variant="rounded" width={325} height={15} />
                          </SliderContainer>
                          <ButtonWrapper>
                            {['All time', '3Y', '1Y', '1M'].map(label => (
                              <Skeleton
                                key={label}
                                variant="rounded"
                                width={60}
                                height={15}
                              />
                            ))}
                          </ButtonWrapper>
                          <BitcoinButtonContainer>
                            <BitcoinButton1
                              href={
                                bitcoinpricesection?.bitcoinSection
                                  ?.buttonLink1 || undefined
                              }
                              target="_blank"
                              onMouseEnter={handleRipple}
                              aria-label="Get Bitcoin"
                            >
                              {bitcoinpricesection?.bitcoinSection?.buttonText1}
                            </BitcoinButton1>
                            <BitcoinButton2
                              href={
                                bitcoinpricesection?.bitcoinSection
                                  ?.buttonLink2 || undefined
                              }
                              onMouseEnter={handleRipple}
                              aria-label="Learn more about Bitcoin"
                            >
                              {bitcoinpricesection?.bitcoinSection?.buttonText2}
                              <GoArrowRight size={20} color="#1A1A1A" />
                            </BitcoinButton2>
                          </BitcoinButtonContainer>
                        </PriceComparisonContaniner>
                      ) : (
                        <PriceComparisonContaniner ref={sentinelRef}>
                          <PriceComparisonContentContaniner>
                            <PriceComparisonContentWrapper>
                              <BitcoinImage
                                src={
                                  bitcoinpricesection?.priceImage?.src ||
                                  undefined
                                }
                                alt={
                                  bitcoinpricesection?.priceImage?.alt ||
                                  undefined
                                }
                              />
                              <PriceComparisonTitle>
                                {bitcoinpricesection.cryptoTitle}
                              </PriceComparisonTitle>
                            </PriceComparisonContentWrapper>
                            <PriceComparisonText>
                              bought
                              {bitcoinValueText(activeButton)}
                              with ${value},
                            </PriceComparisonText>
                            <Text>today, would be worth</Text>
                          </PriceComparisonContentContaniner>
                          <PriceContentWrapperComparison>
                            <PriceTextComparison>
                              {CurrencyFormatter(bitcoinCurrentValue)}
                            </PriceTextComparison>
                            <GainOrLossTextComparison>
                              <span className={isGain ? 'positive' : 'negative'}>
                                {isGain ? (
                                  <>
                                    <GoTriangleUp size={20} />
                                    {NumberFormatter(activeData.gainPercentage)}%
                                  </>
                                ) : (
                                  <>
                                    <GoTriangleDown size={20} />
                                    {NumberFormatter(activeData.gainPercentage)}%
                                  </>
                                )}
                              </span>
                            </GainOrLossTextComparison>
                          </PriceContentWrapperComparison>
                          <SliderContainer>
                            <Slider
                              type="range"
                              min="1"
                              max="1001"
                              step="50"
                              value={value}
                              onChange={event => {
                                handleChange(event);
                              }}
                              percentageValue={calculatePercentage(value)}
                            />
                            <SliderValue>${value}</SliderValue>
                          </SliderContainer>
                          <ButtonWrapper>
                            {(selectedAsset?.monthly_change || 0) < 0
                              ? (['All time', '3Y', '1Y'] as TimeRange[]).map(
                                  label => (
                                    <ButtonGroup
                                      key={label}
                                      active={activeButton === label}
                                      onClick={() => handleButtonClick(label)}
                                    >
                                      {label}
                                    </ButtonGroup>
                                  ),
                                )
                              : (
                                  ['All time', '3Y', '1Y', '1M'] as TimeRange[]
                                ).map(label => (
                                  <ButtonGroup
                                    key={label}
                                    active={activeButton === label}
                                    onClick={() => handleButtonClick(label)}
                                  >
                                    {label}
                                  </ButtonGroup>
                                ))}
                          </ButtonWrapper>
                          <BitcoinButtonContainer>
                            <BitcoinButton1
                              href={
                                bitcoinpricesection?.bitcoinSection
                                  ?.buttonLink1 || undefined
                              }
                              target="_blank"
                              onMouseEnter={handleRipple}
                              aria-label="Get Bitcoin"
                            >
                              {bitcoinpricesection?.bitcoinSection?.buttonText1}
                            </BitcoinButton1>
                            <BitcoinButton2
                              href={
                                bitcoinpricesection?.bitcoinSection
                                  ?.buttonLink2 || undefined
                              }
                              onMouseEnter={handleRipple}
                              aria-label="Learn more about Bitcoin"
                            >
                              {bitcoinpricesection?.bitcoinSection?.buttonText2}
                            </BitcoinButton2>
                          </BitcoinButtonContainer>
                        </PriceComparisonContaniner>
                      )}
                    </ThirdColumn>
                  )}
                </ColumnWrapper>
              </MainWrapperTablet>
              {bitcoinPages.includes(apiTicker) && (
                <CardSection hasBitcoinPages={bitcoinPages.includes(apiTicker)}>
                  {bitcoinpricesection?.cards?.map((card, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Card hasBitcoinPages={bitcoinPages.includes(apiTicker)} key={index}>
                      <CardIcon
                        src={card?.image?.src || undefined}
                        alt={card?.image?.alt || undefined}
                      />
                      <CardTitle>{card?.title}</CardTitle>
                    </Card>
                  ))}
                </CardSection>
              )}
            </MainSection>
          </MainContainer>
        )}
      </StyledContentBlock>
    </StyledSection>
  );
};

export const query = graphql`
  fragment SeoBitcoinPriceSection on Json {
    bitcoinpricesection {
      title
      currentPrice
      graphText
      cryptoTitle
      priceImage {
        src
        alt
      }
      graphImage {
        src
        alt
      }
      bitcoinSection {
        title
        text
        buttonText1
        buttonLink1
        buttonText2
        buttonLink2
      }
      cards {
        title
        image {
          src
          alt
        }
      }
    }
  }
`;

export default BitcoinPriceSection;
